import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { TenantAuthService } from './tenant-auth.service';

// Attaches the tenant JWT only to tenant-portal calls (/api/rms/tenant/**), never to landlord
// calls under /api/rms/** or the site's own /api/**, so the two sessions can never bleed into
// each other. /api/rms/tenant-auth/** (login itself) and /api/rms/passport/public/** (the
// unauthenticated share view) are deliberately left untouched.
export const tenantAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const tenantAuthService = inject(TenantAuthService);
  const router = inject(Router);

  const isTenantPortalCall = req.url.startsWith('/api/rms/tenant/');
  if (!isTenantPortalCall) {
    return next(req);
  }

  const token = tenantAuthService.accessToken();
  const authorizedReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorizedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && tenantAuthService.refreshToken) {
        return tenantAuthService.refresh().pipe(
          switchMap(() => {
            const retriedReq = req.clone({
              setHeaders: { Authorization: `Bearer ${tenantAuthService.accessToken()}` },
            });
            return next(retriedReq);
          }),
          catchError((refreshError) => {
            tenantAuthService.logout();
            router.navigate(['/rms/tenant/login'], { queryParams: { returnUrl: router.url } });
            return throwError(() => refreshError);
          }),
        );
      }
      if (error.status === 401) {
        tenantAuthService.logout();
        router.navigate(['/rms/tenant/login'], { queryParams: { returnUrl: router.url } });
      }
      return throwError(() => error);
    }),
  );
};
